import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { videoSection } from '@/mocks/homeData';

export default function VideoAreaSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 340;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100/50">
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
              {videoSection.title}
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              {videoSection.subtitle}
            </p>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-200 cursor-pointer ${
                canScrollLeft
                  ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-100'
                  : 'border-foreground-200 text-foreground-300 cursor-not-allowed'
              }`}
              aria-label="向左滚动"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-200 cursor-pointer ${
                canScrollRight
                  ? 'border-foreground-300 text-foreground-700 hover:bg-foreground-100'
                  : 'border-foreground-200 text-foreground-300 cursor-not-allowed'
              }`}
              aria-label="向右滚动"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mr-4 md:-mr-6 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videoSection.videos.map((video) => (
            <Link
              key={video.id}
              to="/"
              className="flex-shrink-0 w-[300px] md:w-[360px] snap-start group cursor-pointer"
            >
              <div className="rounded-lg overflow-hidden bg-background-50 border border-foreground-200/40 transition-all duration-300 group-hover:border-accent-300/60">
                <div className="relative h-[200px] md:h-[230px] overflow-hidden">
                  <img
                    src={video.image}
                    alt={video.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-background-50/90 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <i className="ri-play-fill text-accent-500 text-2xl md:text-3xl ml-1"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-3 line-clamp-2 leading-snug group-hover:text-accent-600 transition-colors duration-200">
                    {video.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{video.date}</span>
                    <span className="text-xs text-foreground-600 font-label font-medium">
                      <i className="ri-play-circle-line mr-1"></i>
                      {(video.views / 10000).toFixed(0)}万 播放
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}